import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { checkCacheForPropertyValue } from "../helpers/CacheManagement";
import Header from "../components/LayoutComponents/Header";
import Footer from "../components/LayoutComponents/Footer";
import ScrollToTop from "../components/LayoutComponents/ScrollToTop";
import RouteMetaTags from "../components/Utilities/RouteMetaTags";

// ----- Pages (Lazy loaded for code splitting)
const About = React.lazy(() => import("../pages/About"));
const Donate = React.lazy(() => import("../pages/Donate"));
const Caller = React.lazy(() => import("../pages/Caller"));
const Contact = React.lazy(() => import("../pages/Contact"));
const FAQ = React.lazy(() => import("../pages/FAQ"));
const Generator = React.lazy(() => import("../pages/Generator"));
const Guide = React.lazy(() => import("../pages/Guide"));
const Layouts = React.lazy(() => import("../pages/Layouts"));
const Help = React.lazy(() => import("../pages/Help"));
const NotFound = React.lazy(() => import("../pages/NotFound"));
const Patterns = React.lazy(() => import("../pages/Patterns"));
const Play = React.lazy(() => import("../pages/Play"));
const Privacy = React.lazy(() => import("../pages/Privacy"));
const ReleaseNotes = React.lazy(() => import("../pages/ReleaseNotes"));
const ResetPage = React.lazy(() => import("../pages/Reset"));
const SubmitAPattern = React.lazy(() => import("../pages/SubmitAPattern"));
const Terms = React.lazy(() => import("../pages/Terms"));
const Thanks = React.lazy(() => import("../pages/Thanks"));
const Transparency = React.lazy(() => import("../pages/Transparency"));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    if (window.location.pathname !== "/reset") {
      window.location.replace("/reset");
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

class AppRoutes extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      colorScheme: "dark",
      theme: "lpb",
      hideFooter: false,
      layout: "classic",
    };

    const cachedSettings = checkCacheForPropertyValue("settings");
    if (cachedSettings !== null) {
      if (Object.prototype.hasOwnProperty.call(cachedSettings, "theme")) {
        this.state.theme = cachedSettings.theme;
      }
      if (Object.prototype.hasOwnProperty.call(cachedSettings, "hideFooter")) {
        this.state.hideFooter = cachedSettings.hideFooter;
      }
      if (Object.prototype.hasOwnProperty.call(cachedSettings, "layout")) {
        this.state.layout = cachedSettings.layout;
      }
    }

    const colorScheme = localStorage.getItem("lpb-colorscheme");
    if (colorScheme) {
      this.state.colorScheme = colorScheme;
    }
    document.addEventListener("settingssaved", this.handleSettingsSaved);
    document.addEventListener("resetsettings", this.handleSettingsSaved);
    document.addEventListener("togglecolorscheme", this.toggleColorScheme);
  }

  componentDidMount() {}

  componentWillUnmount() {
    document.removeEventListener("settingssaved", this.handleSettingsSaved);
    document.removeEventListener("resetsettings", this.handleSettingsSaved);
    document.removeEventListener("togglecolorscheme", this.toggleColorScheme);
  }

  toggleColorScheme = (event) => {
    localStorage.setItem("lpb-colorscheme", event.detail);
    this.setState({ colorScheme: event.detail });
  };

  handleSettingsSaved = (event) => {
    const newSettings = event.detail;
    this.setState({
      cardDisplayColor: newSettings.cardDisplayColor,
      theme: newSettings.theme,
      hideFooter: newSettings.hideFooter,
      layout: newSettings.layout,
    });
  };

  render() {
    return (
      <div className={`body-container ${this.state.theme} ${this.state.colorScheme}-scheme`}>
        <BrowserRouter>
          <ErrorBoundary>
            <Header colorScheme={this.state.colorScheme} layout={this.state.layout} />
            <ScrollToTop />
            <RouteMetaTags />
            <React.Suspense fallback={<div className="loading">Loading...</div>}>
              <Routes>
                <Route path="/about" element={<About />} />
                <Route path="/donate" element={<Donate />} />
                <Route exact path="/" element={<Caller />} />
                <Route path="/generator" element={<Generator />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/help" element={<Help />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/layouts" element={<Layouts />} />
                <Route path="/patterns" element={<Patterns />} />
                <Route path="/submitpattern" element={<SubmitAPattern />} />
                <Route path="/play" element={<Play />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/releases" element={<ReleaseNotes />} />
                <Route path="/reset" element={<ResetPage />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/thanks" element={<Thanks />} />
                <Route path="/transparency" element={<Transparency />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </React.Suspense>
            <Footer hideFooter={this.state.hideFooter} />
          </ErrorBoundary>
        </BrowserRouter>
      </div>
    );
  }
}

export default AppRoutes;
